// Chat-history search — server-side filtering, search, and pagination over
// past sessions. Backs the monitor's "Chat history" view (never client-side).

import { apiFetch } from './api';

export interface HistoryItem {
  sessionId: string;
  siteId: string;
  visitorId: string;
  visitorName: string | null;
  visitorEmail: string | null;
  agentId: string | null;
  agentName: string | null;
  medium: 'chat' | 'video';
  startedBy: string;
  startedAt: number;
  endedAt: number | null;
  ownedBy: string;
  starred: boolean;
  status: InboxStatusValue;
  priority: InboxPriorityValue;
  tags: string[];
  departmentId: string | null;
  departmentName: string | null;
  internal: boolean;
  location: string;
}

export type InboxStatusValue = 'open' | 'pending' | 'resolved' | 'closed';
export type InboxPriorityValue = 'low' | 'normal' | 'high' | 'urgent';
/** `agentId` value that selects conversations nobody is assigned to. */
export const UNASSIGNED = 'unassigned';
/** `departmentId` value that selects conversations in no department. */
export const NO_DEPARTMENT = 'none';

export interface HistoryParams {
  q?: string;
  /** An agent id, or UNASSIGNED. */
  agentId?: string;
  status?: InboxStatusValue | '';
  priority?: InboxPriorityValue | '';
  tag?: string;
  /** A department id, or NO_DEPARTMENT. */
  departmentId?: string;
  siteId?: string;
  location?: string;
  engagedOnly?: boolean;
  /** Internal-IP tag filter: 'all' (default), 'only', or 'hide'. */
  internal?: 'all' | 'only' | 'hide';
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
  page?: number;
  limit?: number;
}

export interface HistoryPage {
  items: HistoryItem[];
  total: number;
  page: number;
  limit: number;
}

export async function fetchHistory(params: HistoryParams): Promise<HistoryPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).trim() !== '') qs.set(k, String(v));
  }
  return apiFetch<HistoryPage>(`/api/conversations/search?${qs.toString()}`);
}

export interface HistoryFilters {
  agents: Array<{ id: string; name: string }>;
  countries: string[];
  sites: Array<{ id: string; name: string }>;
  /** Every tag in use on conversations the caller can see. */
  tags: string[];
  departments: Array<{ id: string; name: string; siteId: string }>;
}

export async function fetchHistoryFilters(): Promise<HistoryFilters> {
  return apiFetch<HistoryFilters>('/api/conversations/filters');
}
